/**
 * Who the campaign's virtual page decided it was talking to. Named rather than left implicit in the
 * URL because two of these share a destination: `subscription_unknown` is sent to the free page,
 * but must not be counted as a free account when the flow is reported.
 *
 * - `registered` — arrived via the confirm-email step's own CTA (`?from=confirm-email`).
 * - `free` — a free account.
 * - `subscriber` — holds a paid package.
 * - `subscription_unknown` — the subscription could not be read.
 */
export type BirthdayContestOutcome = 'registered' | 'free' | 'subscriber' | 'subscription_unknown';

const BIRTHDAY_CONTEST_URLS: Record<BirthdayContestOutcome, string> = {
  registered: 'https://www.waipu.tv/gewinnspiel-neu/',
  free: 'https://www.waipu.tv/gewinnspiel-free/',
  subscriber: 'https://www.waipu.tv/gewinnspiel-abo/',
  // Fails open to the free page: a visitor whose subscription can't be read still gets a contest to
  // enter. Reported apart from a free account all the same — an outage must not pass for one.
  subscription_unknown: 'https://www.waipu.tv/gewinnspiel-free/',
};

/**
 * `marker === 'confirm-email'` — set by the confirm-email step's own CTA (`route.entry.ts`'s
 * `cta.href` for this path), forgeable like any search param — wins outright: a visitor who just
 * confirmed their address is also a free account, and `isInFreeSubscription` alone can't tell the
 * two apart.
 */
export function resolveBirthdayContestOutcome(
  marker: 'confirm-email' | undefined,
  isInFreeSubscription: boolean | undefined,
): BirthdayContestOutcome {
  if (marker === 'confirm-email') {
    return 'registered';
  }
  if (isInFreeSubscription === undefined) {
    return 'subscription_unknown';
  }
  return isInFreeSubscription ? 'free' : 'subscriber';
}

/** Every URL is fixed here, never taken from a search parameter. */
export function birthdayContestDestination(outcome: BirthdayContestOutcome): string {
  return BIRTHDAY_CONTEST_URLS[outcome];
}
